import { apiClient } from '../api';

// urlBase64ToUint8Array helper for VAPID applicationServerKey
const urlB64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

let _publicKeyCache = null;
const getPublicKey = async () => {
  if (_publicKeyCache) return _publicKeyCache;
  try {
    const r = await apiClient.get('/push/public-key');
    _publicKeyCache = r.data?.public_key || null;
    return _publicKeyCache;
  } catch (_) {
    return null;
  }
};

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const getPushPermission = () =>
  typeof Notification !== 'undefined' ? Notification.permission : 'denied';

// Register service worker once and reuse
let _swRegPromise = null;
export const ensureServiceWorker = () => {
  if (!_swRegPromise) {
    _swRegPromise = navigator.serviceWorker.register('/sw.js', { scope: '/' });
  }
  return _swRegPromise;
};

export const subscribeToPush = async () => {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'denied' };
  const publicKey = await getPublicKey();
  if (!publicKey) return { ok: false, reason: 'no-vapid-key' };
  const reg = await ensureServiceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey),
      });
    } catch (e) {
      console.warn('Push subscribe failed:', e);
      return { ok: false, reason: 'subscribe-failed' };
    }
  }
  try {
    await apiClient.post('/push/subscribe', sub.toJSON());
    return { ok: true };
  } catch (e) {
    console.warn('Push backend register failed:', e);
    return { ok: false, reason: 'backend-failed' };
  }
};

export const unsubscribeFromPush = async () => {
  if (!isPushSupported()) return { ok: false };
  const reg = await ensureServiceWorker();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  try {
    await apiClient.post('/push/unsubscribe', sub.toJSON());
  } catch (err) {
    // Backend ack failure is non-blocking — we still want to unsubscribe
    // locally so the user's intent is honored. Log for debugging.
    console.warn('Push backend unsubscribe failed:', err?.message || err);
  }
  await sub.unsubscribe();
  return { ok: true };
};
